import { SITE_DOMAINS, Subscription } from '@ritabygglov/core';
import { useCallback, useEffect, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { userService } from '../../../apiServices/UserService';
import { Navbar } from '../../../components/Navbar/Navbar';
import {
  Table,
  TableBody,
  TableHead,
  TableWrapper,
} from '../../../components/Table/Table';
import { useToast } from '../../../context/Toast/useToast';
import { UserData } from '../../../context/User/useUser';
import { useDebouncedInput } from '../../../hooks/useDebouncedInput';
import { useMultiQueryState } from '../../../hooks/useQueryState';
import { AdminPagination } from '../AdminPagination';

const formatSubscriptionStatus = (
  status: Subscription['status'],
  intl: IntlShape
) => {
  const statusMap: Record<Subscription['status'], string> = {
    active: intl.formatMessage({ id: 'admin.users.statusActive' }),
    canceled: intl.formatMessage({ id: 'admin.users.statusCanceled' }),
    incomplete: intl.formatMessage({ id: 'admin.users.statusIncomplete' }),
    incomplete_expired: intl.formatMessage({
      id: 'admin.users.statusIncompleteExpired',
    }),
    past_due: intl.formatMessage({ id: 'admin.users.statusPastDue' }),
    trialing: intl.formatMessage({ id: 'admin.users.statusTrialing' }),
    unpaid: intl.formatMessage({ id: 'admin.users.statusUnpaid' }),
    paused: intl.formatMessage({ id: 'admin.users.statusPaused' }),
  };
  return statusMap[status] || status;
};

const getActiveSubscription = (subscriptions: Subscription[]) => {
  return subscriptions.find(
    (sub) => sub.status === 'active' || sub.status === 'trialing'
  );
};

const getMostRecentSubscription = (subscriptions: Subscription[]) => {
  if (subscriptions.length === 0) return null;
  return subscriptions
    .slice()
    .sort(
      (a, b) =>
        new Date(b.currentPeriodEnd).getTime() -
        new Date(a.currentPeriodEnd).getTime()
    )[0];
};

type SortConfig = {
  field: string;
  order: 'asc' | 'desc';
};

const UsersTable = ({
  users,
  sortConfig,
  onSort,
  onDelete,
}: {
  users: UserData[];
  sortConfig: SortConfig;
  onSort: (field: string) => void;
  onDelete: (userId: number) => void;
}) => {
  const intl = useIntl();
  const getSortIndicator = (field: string) => {
    if (sortConfig.field !== field) return ' ↕';
    return sortConfig.order === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <TableWrapper>
      <Table>
        <colgroup>
          <col style={{ width: '140px' }} />
          <col style={{ width: '200px' }} />
          <col style={{ width: '120px' }} />
          <col style={{ width: '160px' }} />
          <col style={{ width: '160px' }} />
          <col style={{ width: '80px' }} />
          <col style={{ width: '180px' }} />
          <col style={{ width: '80px' }} />
        </colgroup>
        <TableHead>
          <tr>
            <th
              onClick={() => onSort('name')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              {intl.formatMessage({ id: 'admin.users.nameHeader' })}
              {getSortIndicator('name')}
            </th>
            <th
              onClick={() => onSort('email')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              {intl.formatMessage({ id: 'admin.users.emailHeader' })}
              {getSortIndicator('email')}
            </th>
            <th
              onClick={() => onSort('site')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              {intl.formatMessage({ id: 'admin.users.siteHeader' })}
              {getSortIndicator('site')}
            </th>
            <th
              onClick={() => onSort('createdAt')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              {intl.formatMessage({ id: 'admin.users.createdHeader' })}
              {getSortIndicator('createdAt')}
            </th>
            <th
              onClick={() => onSort('lastSeenAt')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              {intl.formatMessage({ id: 'admin.users.lastActiveHeader' })}
              {getSortIndicator('lastSeenAt')}
            </th>
            <th
              onClick={() => onSort('activeDays')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              {intl.formatMessage({ id: 'admin.users.activeDaysHeader' })}
              {getSortIndicator('activeDays')}
            </th>
            <th>
              {intl.formatMessage({ id: 'admin.users.subscriptionHeader' })}
            </th>
            <th>{intl.formatMessage({ id: 'admin.users.actionHeader' })}</th>
          </tr>
        </TableHead>
        <TableBody>
          {users.map((user, index) => {
            const activeSub = getActiveSubscription(user.subscriptions);
            const mostRecentSub = getMostRecentSubscription(user.subscriptions);

            return (
              <tr key={index}>
                <td title={user.name}>
                  <Link to={`/admin/users/${user.userId}/projects`}>
                    {user.name}
                  </Link>
                </td>
                <td title={user.email}>{user.email}</td>
                <td>{SITE_DOMAINS[user.site]}</td>
                <td>
                  {new Date(user.createdAt).toLocaleString('sv-SV', {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  })}
                </td>
                <td>
                  {new Date(user.lastSeenAt).toLocaleString('sv-SV', {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  })}
                </td>
                <td>{user.activeDays}</td>
                <td>
                  {activeSub ? (
                    <span
                      title={`${formatSubscriptionStatus(activeSub.status, intl)}${activeSub.cancelAt ? ` ${intl.formatMessage({ id: 'admin.users.subscriptionCancelAt' }, { date: new Date(activeSub.cancelAt).toLocaleDateString('sv-SE') })}` : ''}`}
                    >
                      {formatSubscriptionStatus(activeSub.status, intl)}
                      {activeSub.cancelAt &&
                        ` ${intl.formatMessage({ id: 'admin.users.subscriptionCancelAt' }, { date: new Date(activeSub.cancelAt).toLocaleDateString('sv-SE') })}`}
                    </span>
                  ) : mostRecentSub ? (
                    <span>
                      {formatSubscriptionStatus(mostRecentSub.status, intl)}
                    </span>
                  ) : (
                    '-'
                  )}
                </td>
                <td>
                  <button
                    onClick={() => onDelete(user.userId)}
                    style={{
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      backgroundColor: 'var(--surface)',
                      color: 'var(--status-red-text-only)',
                      border: 'none',
                      borderRadius: '4px',
                    }}
                  >
                    {intl.formatMessage({ id: 'admin.users.deleteButton' })}
                  </button>
                </td>
              </tr>
            );
          })}
        </TableBody>
      </Table>
    </TableWrapper>
  );
};

export const Users = () => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const { addToast } = useToast();
  const intl = useIntl();

  const [queryState, setQueryState] = useMultiQueryState({
    page: 1,
    limit: 100,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    subscriptionStatus: '',
    site: '',
    search: '',
  });

  const [searchInput, setSearchInput] = useDebouncedInput(
    queryState.search,
    (search) => setQueryState({ search, page: 1 })
  );

  const sortConfig: SortConfig = {
    field: queryState.sortBy,
    order: queryState.sortOrder as 'asc' | 'desc',
  };

  const handleSort = (field: string) => {
    setQueryState({
      sortBy: field,
      sortOrder:
        queryState.sortBy === field && queryState.sortOrder === 'asc'
          ? 'desc'
          : 'asc',
    });
  };

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: queryState.page.toString(),
        limit: queryState.limit.toString(),
        sortBy: queryState.sortBy,
        sortOrder: queryState.sortOrder,
      });

      if (queryState.subscriptionStatus) {
        params.append('subscriptionStatus', queryState.subscriptionStatus);
      }
      if (queryState.site) {
        params.append('site', queryState.site);
      }
      if (queryState.search) {
        params.append('search', queryState.search);
      }

      const { users, totalCount } = await userService.getUsers(
        params.toString()
      );
      setUsers(users);
      setTotalCount(totalCount);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : intl.formatMessage({ id: 'admin.users.fetchError' });
      addToast({
        message: errorMessage,
        type: 'error',
      });
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  }, [queryState, addToast, intl]);

  const handleDeleteUser = async (userId: number) => {
    if (
      !window.confirm(intl.formatMessage({ id: 'admin.users.deleteConfirm' }))
    ) {
      return;
    }

    try {
      const result = await userService.deleteUser(userId);
      addToast({
        message: result.message,
        type: 'success',
      });
      await fetchProjects();
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : intl.formatMessage({ id: 'admin.users.deleteError' });
      addToast({
        message: errorMessage,
        type: 'error',
      });
      console.error('Failed to delete user:', err);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return (
    <div className="admin-page">
      <Navbar />
      <h1>{intl.formatMessage({ id: 'admin.users.title' })}</h1>

      <div style={{ marginBottom: '20px' }}>
        <p>
          {intl.formatMessage(
            { id: 'admin.users.total' },
            { count: <strong>{totalCount}</strong> }
          )}
        </p>
      </div>

      <div
        style={{
          marginBottom: '20px',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: '16px',
          maxWidth: '675px',
        }}
      >
        <div>
          <label
            style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '12px',
              fontWeight: '600',
              textAlign: 'left',
            }}
          >
            {intl.formatMessage({ id: 'admin.users.search' })}
          </label>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'admin.users.searchPlaceholder',
            })}
            style={{
              width: '90%',
              padding: '8px',
              fontSize: '12px',
              border: '1px solid var(--border)',
              borderRadius: '4px',
              backgroundColor: 'var(--surface)',
              color: 'var(--text)',
            }}
          />
        </div>
        <div>
          <label
            style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '12px',
              fontWeight: '600',
              textAlign: 'left',
            }}
          >
            {intl.formatMessage({ id: 'admin.users.filterByStatus' })}
          </label>
          <select
            value={queryState.subscriptionStatus}
            onChange={(e) =>
              setQueryState({ subscriptionStatus: e.target.value, page: 1 })
            }
            style={{
              width: '100%',
              padding: '8px',
              fontSize: '12px',
              border: '1px solid var(--border)',
              borderRadius: '4px',
              backgroundColor: 'var(--surface)',
              color: 'var(--text)',
            }}
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="trialing">Trialing</option>
            <option value="canceled">Canceled</option>
            <option value="paused">Paused</option>
            <option value="past_due">Past Due</option>
            <option value="unpaid">Unpaid</option>
          </select>
        </div>
        <div>
          <label
            style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '12px',
              fontWeight: '600',
              textAlign: 'left',
            }}
          >
            {intl.formatMessage({ id: 'admin.users.filterBySite' })}
          </label>
          <select
            value={queryState.site}
            onChange={(e) => setQueryState({ site: e.target.value, page: 1 })}
            style={{
              width: '100%',
              padding: '8px',
              fontSize: '12px',
              border: '1px solid var(--border)',
              borderRadius: '4px',
              backgroundColor: 'var(--surface)',
              color: 'var(--text)',
            }}
          >
            <option value="">All sites</option>
            <option value="ritabygglov">ritabygglov.se</option>
            <option value="drawblueprint">drawblueprint.com</option>
          </select>
        </div>
      </div>

      <AdminPagination
        page={queryState.page}
        totalCount={totalCount}
        limit={queryState.limit}
        onPageChange={(page) => setQueryState({ page })}
        disabled={loading}
      />

      {loading && users.length === 0 ? (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          {intl.formatMessage({ id: 'admin.users.loading' })}
        </div>
      ) : (
        <>
          <UsersTable
            users={users}
            sortConfig={sortConfig}
            onSort={handleSort}
            onDelete={handleDeleteUser}
          />
          <AdminPagination
            page={queryState.page}
            totalCount={totalCount}
            limit={queryState.limit}
            onPageChange={(page) => setQueryState({ page })}
            disabled={loading}
          />
        </>
      )}
    </div>
  );
};

export default Users;
